/**
 * binb-gallery-sync.js  (BIN-B / Pipeline theme)
 * Daraz-style sync between the product gallery (Flickity) and the variant picker.
 *
 *  IMAGE -> VARIANT : swipe / thumbnail tap / arrow key -> matching variant is selected
 *                     through the theme's own option inputs (price, stock, button, URL,
 *                     swatches and every "theme:variant:change" listener keep working).
 *  VARIANT -> IMAGE : handled by the theme (featured_media). We only add a fallback for
 *                     variants that have no featured media, and stop the theme from
 *                     yanking the gallery while we are the ones changing the variant.
 *
 * Revert: delete this file + the <script> line in snippets/product-info.liquid.
 */
(function () {
  'use strict';

  var BOUND = '__binbGallerySync';

  function norm(s) { return String(s == null ? '' : s).trim().toLowerCase(); }
  function mediaIdOf(fm) { return fm ? String(fm.id != null ? fm.id : fm) : ''; }
  function optName(o) { return o && typeof o === 'object' ? o.name : o; }

  /* ---------- 1. media -> variant(s) mapping (built from the product JSON) ---------- */
  function buildMap(product) {
    var variants = product.variants || [];
    var media = product.media || [];
    var m2v = {};   // mediaId -> [variant]
    var v2m = {};   // variantId -> [mediaId] (gallery order)

    function link(mid, v) {
      mid = String(mid);
      (m2v[mid] = m2v[mid] || []);
      if (m2v[mid].indexOf(v) === -1) m2v[mid].push(v);
    }

    // (a) explicit: Shopify's variant.featured_media
    variants.forEach(function (v) {
      var id = mediaIdOf(v.featured_media);
      if (id) link(id, v);
    });

    // (b) optional: an image whose ALT text exactly equals a variant option value
    //     (e.g. alt "Black" on every Black photo). Only for media with no explicit link.
    media.forEach(function (m) {
      if (m2v[String(m.id)]) return;
      var alt = norm(m.alt);
      if (!alt) return;
      var hits = variants.filter(function (v) {
        return (v.options || []).some(function (o) { return norm(o) === alt; });
      });
      if (hits.length && hits.length < variants.length) {
        hits.forEach(function (v) { link(m.id, v); });
      }
    });

    // reverse index, in gallery order
    media.forEach(function (m) {
      (m2v[String(m.id)] || []).forEach(function (v) {
        (v2m[v.id] = v2m[v.id] || []).push(String(m.id));
      });
    });

    return { m2v: m2v, v2m: v2m };
  }

  /* ---------- 2. choose the best variant among candidates ---------- */
  function pick(cands, current) {
    if (!cands || !cands.length) return null;
    if (current && cands.some(function (v) { return v.id === current.id; })) return current;
    var best = null, bestScore = -1;
    cands.forEach(function (v) {
      var s = 0;
      if (current) {
        (v.options || []).forEach(function (o, i) { if (o === current.options[i]) s++; });
      }
      if (v.available) s += 0.5;
      if (s > bestScore) { bestScore = s; best = v; }
    });
    return best;
  }

  /* ---------- 3. init one product section ---------- */
  function init(container) {
    var slideshow = container.querySelector('[data-product-slideshow]');
    var jsonEl = container.querySelector('[data-product-json]');
    var idInput = container.querySelector('input[name="id"]');
    if (!slideshow || !jsonEl || !idInput || slideshow[BOUND]) return !!(slideshow && slideshow[BOUND]);

    var Fl = window.themeVendor && window.themeVendor.Flickity;
    var flkty = Fl && Fl.data ? Fl.data(slideshow) : null;
    if (!flkty) return false; // theme slider not ready yet -> caller retries

    var product;
    try { product = JSON.parse(jsonEl.innerHTML); } catch (e) { return true; }
    if (!product.variants || product.variants.length < 2) { slideshow[BOUND] = true; return true; }

    var map = buildMap(product);
    if (!Object.keys(map.m2v).length) { slideshow[BOUND] = true; return true; }

    var names = (product.options || []).map(optName);
    var syncing = false;        // we are changing the variant (image -> variant)
    var inOptionChange = false; // an option input fired change (variant -> image window)

    function release() { setTimeout(function () { syncing = false; inOptionChange = false; }, 0); }

    function currentVariant() {
      var id = String(idInput.value);
      for (var i = 0; i < product.variants.length; i++) {
        if (String(product.variants[i].id) === id) return product.variants[i];
      }
      return null;
    }

    function optionInputs(name) {
      var all = container.querySelectorAll('[name^="options["]');
      var out = [];
      for (var i = 0; i < all.length; i++) if (all[i].name === 'options[' + name + ']') out.push(all[i]);
      return out;
    }

    // Select a variant by checking the real option inputs, then fire ONE change event.
    // The theme reads every checked input on that event (price, stock, URL, hooks...).
    function applyVariant(v) {
      var plan = [];
      for (var i = 0; i < names.length; i++) {
        var val = v.options[i], inputs = optionInputs(names[i]), target = null;
        for (var k = 0; k < inputs.length; k++) {
          var el = inputs[k];
          if (el.tagName === 'SELECT') {
            for (var j = 0; j < el.options.length; j++) {
              if (el.options[j].value === val && !el.options[j].disabled) target = { el: el, select: true };
            }
          } else if (el.value === val && !el.disabled) {
            target = { el: el };
          }
        }
        if (!target) return false; // option hidden/sold out/not found -> do not touch anything
        plan.push({ t: target, val: val });
      }
      var changed = null;
      plan.forEach(function (p) {
        if (p.t.select) { if (p.t.el.value !== p.val) { p.t.el.value = p.val; changed = p.t.el; } }
        else if (!p.t.el.checked) { p.t.el.checked = true; changed = p.t.el; }
      });
      if (!changed) return true;
      syncing = true;
      changed.dispatchEvent(new Event('change', { bubbles: true }));
      release();
      return true;
    }

    function activeMediaId() {
      var cell = flkty.selectedElement || (flkty.cells[flkty.selectedIndex] || {}).element;
      return cell ? cell.getAttribute('data-media-id') : '';
    }

    /* IMAGE -> VARIANT */
    flkty.on('change', function (index) {
      var cell = flkty.cells[index];
      var mid = cell && cell.element.getAttribute('data-media-id');
      var cands = mid && map.m2v[mid];
      if (!cands) return; // video / 3D / unmapped image: keep the current variant
      var cur = currentVariant();
      var next = pick(cands, cur);
      if (next && (!cur || next.id !== cur.id)) applyVariant(next);
    });

    /* Gallery protection: the theme re-selects the variant's featured image after ANY option
       change. Block that (a) for changes we caused and (b) when the visible photo already
       belongs to the variant that was just chosen (e.g. viewing an extra shot of it). */
    container.addEventListener('change', function (e) {
      if (e.target && e.target.name && e.target.name.indexOf('options[') === 0) inOptionChange = true;
    }, true);
    container.addEventListener('theme:image:change', function (e) {
      if (syncing) { e.stopPropagation(); return; }
      if (!inOptionChange) return; // thumbnail taps etc. pass through untouched
      var cur = String(activeMediaId()), tgt = String(e.detail && e.detail.id);
      var a = map.m2v[cur], b = map.m2v[tgt];
      if (cur && tgt && cur !== tgt && a && b && a.some(function (v) { return b.indexOf(v) > -1; })) {
        e.stopPropagation();
      }
    }, true);

    /* VARIANT -> IMAGE fallback: variant without featured media but with mapped photos */
    container.addEventListener('theme:variant:change', function (e) {
      if (syncing) return;
      var v = e.detail && e.detail.variant;
      if (!v || mediaIdOf(v.featured_media)) return;
      var list = map.v2m[v.id];
      if (!list || !list.length) return;
      if (list.indexOf(String(activeMediaId())) > -1) return;
      var idx = -1;
      for (var i = 0; i < flkty.cells.length; i++) {
        if (flkty.cells[i].element.getAttribute('data-media-id') === list[0]) { idx = i; break; }
      }
      if (idx > -1) flkty.select(idx);
    });

    slideshow[BOUND] = true;
    return true;
  }

  /* ---------- 4. boot (bounded retries, no polling) ---------- */
  function run() {
    var sections = document.querySelectorAll('[data-section-type="product"]');
    var pending = false;
    Array.prototype.forEach.call(sections, function (c) { if (!init(c)) pending = true; });
    return !pending;
  }

  function boot() {
    if (run()) return;
    [150, 500, 1200, 2500].forEach(function (ms) { setTimeout(run, ms); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  window.addEventListener('load', run, { once: true });
  document.addEventListener('shopify:section:load', function () { setTimeout(boot, 50); });
})();
